import { DocumentItem, FileItem } from '../../../models/polymer/Document.model';
import FileSelectionSegment from '../segments/file-selection-segment';
import { Separator } from '../../../components/separator';
import { Helpers } from '../../../utils/helper';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { reduce, uniq } from 'lodash';
import { P2Alert, P2Modal } from 'react-bootstrap-common-script';
import { UploadFileType } from '../../../models/enum/upload-file-type.enum';

interface DocumentationListsProps{
  document_list:DocumentItem[],
  setFileItemsStorage?: Function,
  filesItemsStorage?: FileItem[],
  type: UploadFileType;
  folderName?: string;
  defaultCheckboxList?: string[];
  setParentFormValid?: React.Dispatch<React.SetStateAction<boolean>>;
  getAdditionalCheckbox?: (additionalCheckbox: string[]) => void;
  forceError?:boolean;
}
function DocumentationLists({folderName, type, filesItemsStorage=[],document_list=[], defaultCheckboxList = [],setFileItemsStorage,forceError, ...props}:DocumentationListsProps) {
  
  // const [filesItemsStorage, setFileItemsStorage] = useState<FileItem[]>([]);

  const [ additionalCheckbox, setAdditionalCheckbox] = useState<string[]>(defaultCheckboxList);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageSize, setMessageSize] = useState<"5MB" | "10MB">("10MB");
  
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  },[]);
  const handleModalTrigger = (type: "5MB" | "10MB") => {
    setMessageSize(type);
    setIsModalOpen(true);
  };
  /**
   * 
   * @param files files that has been selected to upload.
   * @param code documentation code
   * @description to check new incoming and existing files has exceeded certain size.
   * @returns 
   */
  const checkSelectedFilesSize = useCallback((files: FileList | null, code:string, label: string) =>{

    let storageFiles = filesItemsStorage.filter(e=>e.code === code && e.label === label);
    
    let totalSize = reduce(storageFiles.map(e=>e.file_content.size),(sum, n:any)=>{
      return sum + n;
    },0);

    const fileLength = files?.length ?? 0;
    if(files){
      for(let i = 0; i < fileLength; i++){
        let fileContent:File = files[i];
        totalSize += fileContent.size;
      }
    }

    return Helpers.isBytesExceed(totalSize, 10000000);
  },[ filesItemsStorage ]);


  /**
   * 
   * @param event files that has been selected to upload.
   * @param code documentation code
   * @function handleFileChange
   * @description handle the files whenever the user select files to upload, process it and store into the filesItemsStorage state
   * @returns 
   */
  const handleFileChange = useCallback((event:React.ChangeEvent<HTMLInputElement>,  code:string, label: string, file?: File) =>{

    let existingFiles = filesItemsStorage;
    
    const { target: { files } } = event;
    
    const fileLength = files?.length ?? 0;

    if(!checkSelectedFilesSize(files, code, label) && files){
      for(let i = 0; i < fileLength; i++){

        let fileContent:File = files[i];
        

        if(Helpers.isBytesExceed(fileContent.size, 5000000)){
          handleModalTrigger("5MB");
        }
        else{
          let fileItemObj = new FileItem({code:code, label: label, file: file});
          fileItemObj.setFileInfo(fileContent);
          
          const reader = new FileReader();
          reader.readAsDataURL(fileContent);
          reader.onload = (e) => {
            const { result } = reader;
            let base64 = Helpers.stripBase64(result?.toString()) ?? "";
            fileItemObj.setFileBase64(base64)
          };
  
          existingFiles.push(fileItemObj);
        }
      }
      setFileItemsStorage?.([...existingFiles]);
    }
    else{
      handleModalTrigger("10MB");
    }

  },[filesItemsStorage]);

  const assignFileFields = useCallback((file_id: string, submitted_date: string, key: string, id:string)=>{
    let existingFiles = filesItemsStorage;
    let index = existingFiles.findIndex(item => item.file_content.id === id);
    if(index !== -1){
      let newObj = new FileItem(existingFiles[index]);
      newObj.setKey(key);
      newObj.setSubmittedDate(submitted_date);
      newObj.setFileId(file_id);
  
      existingFiles[index] = newObj;
    }
    setFileItemsStorage?.([...existingFiles]);
  },[filesItemsStorage, setFileItemsStorage]);

  /**
   * 
   * @param id id will be the ID of the each file.
   * @function clearFileChange
   * @description handle the files on which to delete from filesItemsStorage
   * @returns 
   */
  const clearFileChange = useCallback((id:string) =>{
    let existingFiles = filesItemsStorage;
    let index = existingFiles.findIndex(e=>e.file_content.id === id);
    if(index !== -1){
      existingFiles.splice(index,1);
    }
      setFileItemsStorage?.([...existingFiles]);
  },[filesItemsStorage])


  const assignError = useCallback((isError: boolean, id:string)=>{
    let existingFiles = [...filesItemsStorage];
    let index = existingFiles.findIndex(item => item.file_content.id === id);
    if(index !== -1){
        existingFiles[index].setIsError(isError);
    }
    setFileItemsStorage?.(existingFiles);
  },[filesItemsStorage]);


  const checkboxClick = useCallback(( label: string )=>{
    let existing_document = additionalCheckbox;
    let found_index = existing_document.findIndex(e=>e === label);
    if(found_index === -1){
      existing_document.push(label);
    }
    else{
      existing_document.splice(found_index,1);
    }
    setAdditionalCheckbox([...existing_document]);
    if(props.getAdditionalCheckbox){
      props.getAdditionalCheckbox(additionalCheckbox);
    }

    const cloned_file_items_storage = [...filesItemsStorage];
    setFileItemsStorage?.(cloned_file_items_storage.filter(item => item.label !== label));
  },[additionalCheckbox, filesItemsStorage])


  /**
   * Perform validation check whether all the required documentation are uploaded.
   * How : get the unique codes from document_list that are required to upload, then check the unique code from fileItemsStorage. If all codes presented from uploaded_docs compare to required_docs, then valid.
   */
  const performValidityCheck = useCallback(()=>{
    let required_docs = uniq(document_list.map(item=>item.label));
    let uploaded_docs =  uniq(filesItemsStorage.filter(item => item.file_id).map(item=>item.label));

    let status = required_docs.every(e=>{
      if(uploaded_docs.includes(e) || additionalCheckbox.includes(e)){
        return true;
      }
      else{
        return false;
      }
    });

    if(status){
      status = filesItemsStorage.every(item => item.file_id !== "");
    }

    if(props.setParentFormValid){
      props.setParentFormValid(status);
    }

    
  },[document_list, filesItemsStorage, props, additionalCheckbox])

  useEffect(()=>{
    performValidityCheck();
  },[filesItemsStorage, additionalCheckbox, performValidityCheck])

  const modalMessage = messageSize === '10MB' ? `You have now exceeded the maximum of 10MB worth of files. Please select another file or reduce the file size.` : `You have exceeded the maximum file size of 5MB. Please select another file or reduce the file size.`;

  return (
    <div>
      {document_list.map((document: DocumentItem, _index: number) => {
        const files = filesItemsStorage.filter((file) => file.label === document.label && file.code === document.code);
        const key = _index;
        return (
          <Fragment key={key}>
            {key >= 1 && <Separator style={{ margin: "24px 0" }} />}
            <FileSelectionSegment
              type={type}
              folderName={folderName}
              index={key}
              document={document}
              files={files}
              clearFileChange={clearFileChange}
              handleFileChange={handleFileChange}
              assignFileFields={assignFileFields}
              assignError={assignError}
              additionalCheckbox={additionalCheckbox}
              additionalCheckboxOnClick={checkboxClick}
            />
          {forceError && (files.length === 0 || files.some(item => !item.file_id)) &&           
            <div style={{ marginTop: 24 }}>
              <P2Alert variant={"danger"} text={"Please ensure files are uploaded successfully."}></P2Alert>
            </div>
          }
          </Fragment>
        );
      })}
      <div className="polymer-small-width">
        <P2Modal isOpen={isModalOpen} onCloseModal={closeModal} variant="close-button" title="Maximum file size limit exceeded" children={<p>{modalMessage}</p>} />
      </div>
    </div>
  );
}

export default DocumentationLists